// Agendamiento · Ventas
//
// Pagos aprobados por día (hora de Chile), medio de pago, recinto y servicio, para cruzar con
// Transbank y Bsale. Se agrupa por la fecha del pago, que es cuando entró la plata,
// no por la fecha de la atención.
//
// Ojo: estas ventas todavía NO están sumadas en el cuadre de caja de Operaciones.

import React, { useCallback, useEffect, useState } from 'react';
import { DatePicker, Table } from 'antd';
import { FiCreditCard, FiDollarSign, FiDownload, FiFileText, FiInfo, FiSmartphone } from 'react-icons/fi';
import dayjs from 'dayjs';
import Agenda from '../../../../service/Agenda';
import { mensajeDeError } from '../../../../service/client';
import { useNotify } from '../../../../providers/NotificationProvider';
import useAuthContext from '../../../../hooks/useAuthContext';
import { exportarHojaUnica } from '../../../../utils/exportExcel';
import { EncabezadoAdmin, TarjetaResumen, ChipNeutro, claseBtnSecundario } from '../../../Ui/Admin/PiezasAdmin';
import { BarraFiltros, Chip, Vacio } from './piezas';
import { MEDIO, pesos } from './util';

const { RangePicker } = DatePicker;

export default function Ventas() {
  const notify = useNotify();
  const { user } = useAuthContext();
  const [rango, setRango] = useState([dayjs().startOf('month'), dayjs()]);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setData(await Agenda.ventas({ desde: rango[0].format('YYYY-MM-DD'), hasta: rango[1].format('YYYY-MM-DD') }));
    } catch (err) {
      notify.error(mensajeDeError(err, 'No se pudieron cargar las ventas'));
    } finally {
      setCargando(false);
    }
  }, [rango, notify]);

  useEffect(() => { cargar(); }, [cargar]);

  const filas = data?.filas || [];
  const suma = (medio) => filas.filter(f => f.medio === medio).reduce((s, f) => s + f.monto, 0);
  const pendientes = filas.reduce((s, f) => s + f.boletasPendientes, 0);

  const exportar = async () => {
    try {
      if (!filas.length) { notify.warning('No hay ventas para exportar'); return; }
      await exportarHojaUnica({
        archivo: 'ventas-servicios',
        titulo: 'Ventas de servicios con hora',
        subtitulo: 'Pagos aprobados por día, medio de pago, recinto y servicio (hora de Chile)',
        usuario: user,
        filtros: { 'Desde': rango[0].format('DD/MM/YYYY'), 'Hasta': rango[1].format('DD/MM/YYYY') },
        nombreHoja: 'Ventas',
        columnas: [
          { header: 'Día', key: 'dia', tipo: 'texto' },
          { header: 'Medio de pago', key: 'medio', tipo: 'texto' },
          { header: 'Recinto', key: 'recinto', tipo: 'texto' },
          { header: 'Servicio', key: 'servicio', tipo: 'texto' },
          { header: 'Pagos', key: 'cantidad', tipo: 'numero', total: true },
          { header: 'Monto', key: 'monto', tipo: 'moneda', total: true },
          { header: 'Boletas pendientes', key: 'pendientes', tipo: 'numero', total: true },
        ],
        filas: filas.map(f => ({
          dia: dayjs(f.dia).format('DD/MM/YYYY'), medio: MEDIO[f.medio] || f.medio,
          recinto: f.recinto, servicio: f.servicio,
          cantidad: f.cantidad, monto: f.monto, pendientes: f.boletasPendientes,
        })),
      });
    } catch { notify.error('Error al generar Excel'); }
  };

  const columnas = [
    {
      title: 'Día', dataIndex: 'dia', key: 'dia', width: 170,
      render: (d) => <span className="font-bold text-gray-900 tabular-nums capitalize">{dayjs(d).format('ddd DD/MM/YYYY')}</span>,
    },
    { title: 'Medio de pago', dataIndex: 'medio', key: 'medio', width: 180, render: (m) => <ChipNeutro>{MEDIO[m] || m}</ChipNeutro> },
    { title: 'Recinto', dataIndex: 'recinto', key: 'recinto', width: 200 },
    { title: 'Servicio', dataIndex: 'servicio', key: 'servicio', width: 200 },
    { title: 'Pagos', dataIndex: 'cantidad', key: 'cantidad', width: 110, align: 'right', render: (n) => <span className="tabular-nums">{n}</span> },
    { title: 'Monto', dataIndex: 'monto', key: 'monto', width: 160, align: 'right', render: (m) => <span className="font-bold text-gray-900 tabular-nums">{pesos(m)}</span> },
    {
      title: 'Boletas', dataIndex: 'boletasPendientes', key: 'boletas', width: 170,
      render: (n) => n ? <Chip tono="amarillo">{n} sin emitir</Chip> : <Chip tono="azul">Todas emitidas</Chip>,
    },
  ];

  return (
    <div>
      <EncabezadoAdmin
        titulo="Ventas de"
        destacado="servicios"
        subtitulo={`${data?.total?.cantidad ?? 0} pagos aprobados del ${rango[0].format('DD/MM')} al ${rango[1].format('DD/MM')}`}
      >
        <button type="button" onClick={exportar} disabled={!filas.length} className={claseBtnSecundario}>
          <FiDownload size={15} /> Exportar Excel
        </button>
      </EncabezadoAdmin>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <TarjetaResumen icon={<FiDollarSign size={19} />} label="Total" value={pesos(data?.total?.monto)} />
        <TarjetaResumen icon={<FiCreditCard size={19} />} label="Débito" value={pesos(suma('debito'))} />
        <TarjetaResumen icon={<FiSmartphone size={19} />} label="Crédito y prepago" value={pesos(suma('credito') + suma('prepago'))} />
        <TarjetaResumen icon={<FiFileText size={19} />} label="Boletas sin emitir" value={pendientes} />
      </div>

      <BarraFiltros>
        <RangePicker value={rango} onChange={(v) => v && setRango(v)} allowClear={false} format="DD/MM/YYYY" aria-label="Rango de fechas" />
        <span className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
          <FiInfo size={13} /> Todavía no se suman al cuadre de caja de Operaciones: cuádralas aparte con Transbank y Bsale
        </span>
      </BarraFiltros>

      <div className="admin-table-card">
        <Table
          columns={columnas} dataSource={filas} loading={cargando} rowKey={(f) => `${f.dia}-${f.medio}-${f.recinto}-${f.servicio}`} sticky
          pagination={filas.length > 31 ? { pageSize: 31, style: { padding: '12px 16px' } } : false}
          scroll={{ x: 1100 }}
          locale={{ emptyText: <Vacio titulo="Sin ventas en el rango" texto="Prueba con otras fechas." /> }}
          summary={(rows) => rows.length > 1 && (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}><span className="font-extrabold">Total</span></Table.Summary.Cell>
              <Table.Summary.Cell index={1} />
              <Table.Summary.Cell index={2} />
              <Table.Summary.Cell index={3} />
              <Table.Summary.Cell index={4} align="right"><span className="font-extrabold tabular-nums">{data?.total?.cantidad}</span></Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right"><span className="font-extrabold tabular-nums">{pesos(data?.total?.monto)}</span></Table.Summary.Cell>
              <Table.Summary.Cell index={6} />
            </Table.Summary.Row>
          )}
        />
      </div>
    </div>
  );
}
